<template>
  <v-card
    :class="[
      { 'ml-auto mr-4': !isGuest },
      { 'ml-4': isGuest },
      'my-2 overflow-y-auto',
    ]"
    :max-width="isMobile ? '85%' : '60%'"
    :max-height="800"
    elevation="0"
  >
    <v-card-text
      :class="{
        'pa-0': !hasMemoryUpdates(msg),
        'pa-1': hasMemoryUpdates(msg),
      }"
    >
      <div
        :class="[
          'd-flex',
          'align-center',
          { 'flex-row-reverse': !isGuest },
          'text-caption',
          'black--text',
          'mb-0',
        ]"
      >
        <ai-train-modal
          v-if="
            !isGuest &&
            !msg.auto_message_rule_id &&
            conversation.active_reservation
          "
          :train-messages="trainMessages"
          :conversation="conversation"
          :msg="msg"
        />
        <span v-if="msg.auto_message_rule_id" class="font-weight-medium"
          >Auto</span
        >
        <span v-else class="font-weight-medium">{{
          isGuest ? conversation.guest_full_name : msg.rep_name
        }}</span>
        <v-icon v-if="msg.msg_type">mdi-circle-small</v-icon>
        <span v-if="msg.msg_type"> via {{ msg.msg_type }} </span>
      </div>
      <div class="d-flex">
        <v-sheet
          :class="[
            { 'grey lighten-4': !isGuest && !isNote && !isFailed },
            { 'info lighten-5': isGuest && !isFailed },
            { 'notification lighten-5': isNote },
            { 'rounded-tl-0': isGuest, 'rounded-tr-0': !isGuest },
            { 'msg-failed': isFailed },
          ]"
          class="mx-2 pa-4 font-weight-medium flex rounded-xl overflow-y-auto"
          :elevation="isGuest ? 3 : 0"
          rounded
        >
          <div
            class="p-relative"
            :class="[{ trw: collapsable, 'height-trans': collapsed }]"
          >
            <div
              class="msg-box font-weight-medium text-caption text-md-body-2"
              v-html="sanitize(switchNewLine(msg.body))"
            />
            <!-- XSS-safe: local sanitize() wraps $domPurify.sanitize with custom config -->
          </div>
          <div v-if="collapsable" class="d-flex justify-end flex align-end">
            <v-btn plain text @click="onCollapse">
              <v-icon>{{
                collapsed ? 'fas fa-caret-up' : 'fas fa-caret-down'
              }}</v-icon>
            </v-btn>
          </div>
          <div v-if="msg.recording_url" class="mt-2">
            <audio
              controls
              class="small-audio-player"
              :src="msg.recording_url"
            ></audio>
          </div>
        </v-sheet>
        <div style="display: flex; flex-direction: column">
          <v-tooltip v-if="msg.ai_translation" left>
            <template #activator="{ on }">
              <v-icon small v-on="on"> mdi-translate </v-icon>
            </template>
            <span>{{ msg.ai_translation }}</span>
          </v-tooltip>
          <v-tooltip v-if="imageAnalysisText" left max-width="400">
            <template #activator="{ on }">
              <v-icon small v-on="on"> mdi-image-search-outline </v-icon>
            </template>
            <span style="white-space: pre-line">{{ imageAnalysisText }}</span>
          </v-tooltip>
          <v-tooltip v-if="transcodedAudios(msg)" left>
            <template #activator="{ on }">
              <v-icon small v-on="on"> mdi-transcribe </v-icon>
            </template>
            <span>{{ transcodedAudios(msg) }}</span>
          </v-tooltip>

          <v-tooltip v-if="msg.en_translation" left>
            <template #activator="{ on }">
              <v-icon small v-on="on"> mdi-translate-variant </v-icon>
            </template>
            <span>{{ msg.en_translation }}</span>
          </v-tooltip>

          <v-tooltip v-if="isSuperAdmin && hasMemoryUpdates(msg)" left>
            <template #activator="{ on }">
              <v-icon
                small
                color="blue darken-1"
                v-on="on"
                @click="showMemoryUpdates(msg)"
              >
                mdi-notebook-edit-outline
              </v-icon>
            </template>
            <span>Memory Updates</span>
          </v-tooltip>
        </div>
      </div>

      <div
        :class="[
          'd-flex',
          { 'justify-end': !isGuest },
          'align-center',
          'text-caption',
          'grey--text',
          'mt-1',
        ]"
      >
        <div v-if="msg.is_pre_made">
          <v-chip color="grey lighten-4" x-small>
            <span class="secondary--text">Premade</span>
          </v-chip>
          <v-icon>mdi-circle-small</v-icon>
        </div>
        <span v-if="isToday(msg.sent_at)">
          {{ parseDateTZ(msg.sent_at, 'hh:mm A', timezone) }}
        </span>
        <span v-else>
          {{ parseDateTZ(msg.sent_at, 'D MMM YYYY, hh:mm A', timezone) }}
        </span>
        <span v-if="msg.status" class="ml-2">
          <v-icon
            v-if="['read', 'delivered'].includes(msg.status)"
            dark
            :color="msg.status === 'read' ? 'blue' : 'grey'"
            small
            >mdi-check-all</v-icon
          >
          <v-icon
            v-if="['queued'].includes(msg.status)"
            dark
            color="grey"
            small
          >
            mdi-progress-clock
          </v-icon>
          <v-icon v-if="msg.status === 'sent'" dark color="grey" small
            >mdi-check</v-icon
          >
          <v-tooltip left>
            <template #activator="{ on }">
              <v-icon
                v-if="isFailed"
                color="error"
                v-on="
                  msg.error_msg &&
                  (!msg.error_msg.startsWith('error_code') || isSuperAdmin) &&
                  on
                "
                >mdi-alert-circle
              </v-icon>
            </template>
            <div
              v-if="
                !msg.error_msg ||
                !msg.error_msg.startsWith('error_code') ||
                isSuperAdmin
              "
              class="msg-error"
            >
              {{ msg.error_msg }}
            </div>
          </v-tooltip>
        </span>
      </div>
    </v-card-text>
    <ai-memory-updates-dialog
      v-if="showMemoryModal"
      :show="showMemoryModal"
      :memory-data="currentMemoryUpdates"
      @close="showMemoryModal = false"
    />
  </v-card>
</template>

<script>
import CommonFunctions from 'components/mixins/common_functions'
import PermissionsMixin from 'components/mixins/permissions-mixin'
import deviceMixin from 'components/mixins/device-mixin'
import AiTrainModal from 'components/conversations/ai-train-modal.vue'
import BoomFeature from 'components/mixins/boom-feature'
import AiMemoryUpdatesDialog from 'components/ai/ai-memory-updates-dialog.vue'

export default {
  name: 'MsgBox',
  mixins: [CommonFunctions, PermissionsMixin, deviceMixin, BoomFeature],
  props: ['msg', 'conversation', 'collapsable', 'trainMessages', 'a'],
  components: { AiTrainModal, AiMemoryUpdatesDialog },

  data() {
    return {
      collapsed: !this.collapsable,
      showMemoryModal: false,
      currentMemoryUpdates: null,
    }
  },
  computed: {
    linkRegex() {
      return /((https?|ftps?):\/\/[^"'<(,)>\s]+)(?![^<>]*>|[^"]*?<\/a)/gim
    },
    timezone() {
      return this.$store.state.app.configuration.timezone
    },
    isNote() {
      return this.msg.msg_type === 'note'
    },
    isGuest() {
      return this.msg.by === 'guest' || this.msg.by === 'guesty'
    },
    isFailed() {
      return ['undelivered', 'failed'].includes(this.msg.status)
    },
    // AI descriptions of the images attached inside a channel/email message
    // (#27621). `img` messages show theirs on the image itself.
    imageAnalysisText() {
      const images = this.msg?.image_analysis?.images
      if (this.msg?.msg_type === 'img' || !Array.isArray(images)) return ''
      const descriptions = images
        .filter(
          img =>
            img &&
            img.status === 'ok' &&
            typeof img.detailed_description === 'string' &&
            img.detailed_description
        )
        .map(img => img.detailed_description)
      if (descriptions.length < 2) return descriptions[0] || ''
      return descriptions.map((d, i) => `${i + 1}) ${d}`).join('\n\n')
    },
  },
  methods: {
    firstLine(message) {
      return this.switchNewLine(message).split('<br>')[0]
    },
    isImageUrl(url) {
      if (!url) return false
      if (/\.(jpg|jpeg|png|gif|webp|bmp)(\?.*)?$/i.test(url)) return true
      if (/res\.cloudinary\.com\/.*\/image\/upload/i.test(url)) return true
      if (/muscache\.com\/im\/pictures/i.test(url)) return true
      return false
    },
    switchNewLine(message) {
      if (message) {
        if (message.startsWith('<!')) {
          const div = document.createElement('div')
          div.innerHTML = message
          const elements = div.getElementsByTagName('base')
          while (elements[0]) elements[0].parentNode.removeChild(elements[0])
          message = div.innerHTML
        }
        return message
          .replace(/(?:\r\n|\r|\n)/g, '<br>')
          .replace(this.linkRegex, (match, url) => {
            if (this.isImageUrl(url)) {
              const src = this.cloudinaryDelivery(url, { width: 1000 })
              return `<img src="${src}" alt="image" style="max-width: 500px; border-radius: 8px;" />`
            }
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
          })
      }
    },
    sanitize(html) {
      if (!html) return ''
      return this.$domPurify.sanitize(html, {
        ADD_TAGS: ['video'],
        ADD_ATTR: ['target', 'controls'],
      })
    },
    onCollapse() {
      this.collapsed = !this.collapsed
    },
    transcodedAudios(message) {
      if (message.transcoded_audios && message.transcoded_audios.length) {
        return message.transcoded_audios.join('<br>')
      }
    },
    hasMemoryUpdates(msg) {
      return msg.memory_updated
    },
    showMemoryUpdates(msg) {
      this.currentMemoryUpdates = msg.handled_facts || []
      this.showMemoryModal = true
    },
  },
}
</script>
<style lang="scss">
.trw {
  max-height: 25px;
  transition: max-height 0.25s ease-out;
  overflow: hidden;
}
.height-trans {
  max-height: 1000px;
  transition: max-height 0.75s ease-in;
}
.guest-box {
  border-top-left-radius: 0 !important;
}
.host-box {
  border-top-right-radius: 0 !important;
}
.msg-box {
  word-break: break-word;
  max-height: 1000px;
  overflow-y: auto;
}

.msg-box img {
  max-width: 500px;
}
.msg-box >>> a {
  color: blue;
}
.msg-error {
  width: 350px;
}
.msg-failed {
  background-color: #fff7f8 !important;
  border-color: #fff7f8 !important;
}
.tooltip {
  margin-top: -8px;
}
.tooltip .tooltipicon {
  font-size: 20px;
}
.small-audio-player {
  height: 30px !important;
}
</style>
